import { v4 as uuid } from 'uuid'
import { memo, MouseEventHandler, useCallback } from 'react'
import { ListItemText, MenuItem } from '@mui/material'
import FavouriteIcon from '../../assets/icons/favourite'
import Rename from '../../assets/icons/Rename'
import BinIcon from '../../assets/icons/BinIcon'
import { UltraSmallButton } from '../custom'
import { generatePath, useNavigate } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import { saveUserDashboard, updateUserDashboardDefault } from '../../services/apis/dashboard'
import { useDispatch, useSelector } from 'react-redux'
import { selectUserDashboards } from '../../selectors/layout'
import { WidgetConfig } from '../widgetDashboard/widgetDashboard.interface'
import { selectUnderlyingOptionsExpiriesAll } from '../../selectors/options'
import { selectSnapshots } from '../../selectors/security'
import store from '../../store'
import updateUtils from '../multiPaneMenu/utils/updateSecurityUtil'
import { updateSavedChartConfigs } from '../../reducer/chart.reducer'
import { updateLayout } from '../../reducer/widgetDashboard.reducer'
import { setDefaultDashboard, setUserDashboards } from '../../reducer/user.reducer'
import { DashboardShareStatus, Platform, platformFromJSON, UserDashboardProto } from '../../proto/generated/user-preference'
import { selectIsMobile } from '../../selectors/device'
import { normalizePlatform } from '../widgetDashboard/widgetDashboard.helper'
import { getSecurityAsync } from '../../util/security'

const layoutMenuActionIconSx = { fontSize: 18, width: 18, height: 18 } as const

const layoutMenuActionButtonSx = {
    visibility: 'hidden' as const,
    '& svg': layoutMenuActionIconSx,
} as const


export type LayoutMenuItemProps = {
    dashboard: UserDashboardProto,
    active: boolean,
    onRenameClick: (id: string) => void,
    onDeleteClick: (id: string) => void,
    closeMenu: () => void
}

const LayoutMenuItem: React.FC<LayoutMenuItemProps> = ({dashboard, active, onRenameClick, onDeleteClick, closeMenu}) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const dashboards = useSelector(selectUserDashboards);
    const isMobile = useSelector(selectIsMobile)
    const platform = isMobile?Platform.MOBILE:Platform.DESKTOP
    const isPlatformDifferent = (isMobile && platformFromJSON(dashboard.platform) !== Platform.MOBILE) || (!isMobile && platformFromJSON(dashboard.platform) === Platform.MOBILE);
    const platformDashboards = dashboards.filter(d => !d.isPredefined && normalizePlatform(d.platform) === platform);

    const onDeleteClickHandler: MouseEventHandler = useCallback((e) => {
        e.stopPropagation()
        onDeleteClick(dashboard.id);
    }, [dashboard.id, onDeleteClick])

    const onSetAsDefaultClick: MouseEventHandler = useCallback(async (e) => {
        e.stopPropagation();
        const updatedDashboard = await updateUserDashboardDefault({ dashboard });
        dispatch(setDefaultDashboard({ id: updatedDashboard.id, platform: isMobile ? Platform.MOBILE : Platform.DESKTOP }))
    }, [dashboard, dispatch, isMobile])

    const onPredefinedDashboardClicked = useCallback(async () => {
        const state = store.getState()
        const optionsExpiriesAll = selectUnderlyingOptionsExpiriesAll()(state)!!
        const underlyingSnaphots = selectSnapshots(state)
        const { id, layout, name } = dashboard
        const newLayoutId = uuid().replace(/[-]*/g, '').split('').slice(0, 10).join('')
        const updatedLayout = layout.replace(new RegExp(id, 'g'), newLayoutId)
        const updatedParsedLayout = JSON.parse(updatedLayout)
        const charts = {
            ...updatedParsedLayout.charts
        }
        const drawings = {
            ...updatedParsedLayout.drawings
        }
        const layoutToBeSaved = {
            panes: updatedParsedLayout.panes,
            syncSettings: updatedParsedLayout.syncSettings,
            widgets: updatedParsedLayout.widgets,
            drawings: updatedParsedLayout.drawings,
            charts: updatedParsedLayout.charts
        }
        const widgets: Record<string, WidgetConfig<any>> = updatedParsedLayout.widgets
        for (const w_key of Object.keys(widgets)) {
            const type = layoutToBeSaved.widgets[w_key].widgetType
            const key = widgets[w_key as string]?.settings?.key
            const optionExpiriesData = optionsExpiriesAll[key] ? [optionsExpiriesAll[key]] : undefined;
            const underlyingSnaphot = underlyingSnaphots[key]
            const nearAtm = layoutToBeSaved.charts[`${newLayoutId}-${w_key}`]?.multiStrikeChartSettings?.nearAtm
            const security = await getSecurityAsync(key);
            const updates = await updateUtils(
                dispatch,
                newLayoutId,
                layoutToBeSaved,
                optionExpiriesData,
                security,
                key ?? "NSE:NIFTY_50",
                underlyingSnaphot,
                type,
                w_key,
                nearAtm ?? 3,
            );
            // update chart-multiStrikeConfig
            if (
                Object.keys(updates?.securities ?? {}).length > 0 &&
                layoutToBeSaved.charts[`${newLayoutId}-${w_key}`] &&
                layoutToBeSaved.charts[`${newLayoutId}-${w_key}`]?.multiStrikeChartSettings
            ) {
                layoutToBeSaved.charts = {
                    ...layoutToBeSaved.charts,
                    [`${newLayoutId}-${w_key}`]: {
                        ...layoutToBeSaved.charts[`${newLayoutId}-${w_key}`],
                        multiStrikeChartSettings: {
                            ...layoutToBeSaved.charts[`${newLayoutId}-${w_key}`]?.multiStrikeChartSettings,
                            ...(nearAtm ? { nearAtm: nearAtm } : { securities: updates?.securities }),
                            securities: updates?.securities,
                            expiry: updates?.expiry,
                            key: key
                        }
                    }
                };
            }
        }

        dispatch(updateSavedChartConfigs({
            charts,
            drawings
        }))
        

        try {
            const result = await saveUserDashboard({
                dashboard: {
                    id: newLayoutId,
                    layout: JSON.stringify(layoutToBeSaved),
                    name: name,
                    isDefault: dashboard?.isDefault || false,
                    isPredefined: false,
                    shareState: DashboardShareStatus.NOT_SHARED,
                    platform: isMobile ? Platform.MOBILE : Platform.DESKTOP
                }
            });
            if (!dashboards.find((d) => d.id === newLayoutId)?.name) {
                dispatch(setUserDashboards([...dashboards, result]))
            }
            const copiedLayout = {
                name: `${name}`,
                panes: layoutToBeSaved.panes,
                singleMode: layoutToBeSaved.panes.panes.length === 1,
                syncSettings: layoutToBeSaved.syncSettings,
                widgets: layoutToBeSaved.widgets,
                drawings: layoutToBeSaved.drawings,
                charts: layoutToBeSaved.charts
            }
            dispatch(updateLayout({ id: newLayoutId, layout: copiedLayout }))
            navigate(generatePath(ROUTES.chart, { layoutId: newLayoutId }))
        } catch (error) {
            console.error("Unexpected error:", error);
        }

    }, [dashboard, dashboards, dispatch, isMobile, navigate])

    const onClick: MouseEventHandler = useCallback(async (e) => {
        e.stopPropagation()
        closeMenu();
        if (dashboard.isPredefined) {
            onPredefinedDashboardClicked();
        } else {
            navigate(generatePath(ROUTES.chart, { layoutId: dashboard.id }))
        }
    }, [closeMenu, dashboard.isPredefined, dashboard.id, onPredefinedDashboardClicked, navigate]);

    const onRenameClickHandler = useCallback(() => {
        onRenameClick(dashboard.id)
    }, [dashboard, onRenameClick])

    return <>
            <MenuItem
                key={dashboard.id}
                selected={active}
                onClick={isPlatformDifferent ? undefined : onClick}
                data-event={'Saved Layout Selected'}
                data-event-source={'save-menu'}
                data-event-layout={dashboard.name}
                sx={{
                    '&:hover .layout-menu-action-button': {
                        visibility: 'visible'
                    },
                }}
            >
                <ListItemText primary={dashboard.name} primaryTypographyProps={{ color: isPlatformDifferent ? 'text.secondary' : 'text.primary' }} />
                {!dashboard.isPredefined  ? <>
                    {platformDashboards.length > 1 ? <UltraSmallButton
                        color="inherit"
                        className="layout-menu-action-button"
                        sx={{ ...layoutMenuActionButtonSx, mr: 0.25 }}
                        onClick={onDeleteClickHandler}
                        disabled={isPlatformDifferent}
                    >
                        <BinIcon sx={layoutMenuActionIconSx} />
                    </UltraSmallButton> : null}
                    <UltraSmallButton
                        color="inherit"
                        className="layout-menu-action-button"
                        sx={{ ...layoutMenuActionButtonSx, mr: 0.5 }}
                        onClick={onRenameClickHandler}
                    >
                        <Rename sx={layoutMenuActionIconSx} />
                    </UltraSmallButton>
                    <UltraSmallButton
                        color="inherit"
                        className="layout-menu-action-button"
                        sx={{
                            ...layoutMenuActionButtonSx,
                            visibility: dashboard.isDefault ? 'visible' : 'hidden',
                        }}
                        onClick={onSetAsDefaultClick}
                        disabled={isPlatformDifferent}
                    >
                        <FavouriteIcon filled={dashboard.isDefault} sx={layoutMenuActionIconSx} />
                    </UltraSmallButton>
                </>: null}
                
            </MenuItem>
        </>
};

export default memo(LayoutMenuItem);